if (!customElements.get('blog-search')) {
  class BlogSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.blogHandle = this.getAttribute('data-blog-handle') || '';
      this.blogPrefix = `/blogs/${this.blogHandle}/`;
      this.searchUrl = '/search/suggest.json';
      this.input = this.querySelector('.blog-search__input');
      this.results = this.querySelector('.blog-search__results');
      this.resetBtn = this.querySelector('.blog-search__reset');
      this.selectedIndex = -1;
      this.abortController = null;
    }

    connectedCallback() {
      if (!this.input) return;
      this.input.addEventListener('input', debounce(this.handleInput.bind(this), 300));
      this.input.addEventListener('keydown', this.handleKeydown.bind(this));
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      if (this.resetBtn) {
        this.resetBtn.addEventListener('click', this.handleReset.bind(this));
      }
      document.addEventListener('click', this.handleClickOutside.bind(this));
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleClickOutside.bind(this));
    }

    handleInput() {
      const query = this.input.value.trim();
      this.resetBtn?.classList.toggle('blog-search__reset--visible', query.length > 0);

      if (query.length < 2) {
        this.close();
        return;
      }
      this.fetchResults(query);
    }

    handleFocus() {
      const query = this.input.value.trim();
      if (query.length >= 2 && this.results.innerHTML.trim()) {
        this.open();
      }
    }

    handleReset() {
      this.input.value = '';
      this.close();
      this.resetBtn?.classList.remove('blog-search__reset--visible');
      this.input.focus();
    }

    handleKeydown(e) {
      if (e.key === 'Enter') {
        const items = this.results.querySelectorAll('.blog-search__result-item');
        e.preventDefault();
        if (this.selectedIndex >= 0 && items.length) {
          const link = items[this.selectedIndex]?.querySelector('a');
          if (link) window.location.href = link.href;
        } else {
          this.submitSearch();
        }
        return;
      }

      const items = this.results.querySelectorAll('.blog-search__result-item');
      if (!items.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1);
        this.updateSelection(items);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.updateSelection(items);
      } else if (e.key === 'Escape') {
        this.close();
        this.input.blur();
      }
    }

    submitSearch() {
      const query = this.input.value.trim();
      if (!query) return;
      const params = new URLSearchParams({
        q: query,
        type: 'article',
      });
      if (this.blogHandle) params.set('scope', this.blogHandle);
      window.location.href = `/search?${params}`;
    }

    updateSelection(items) {
      items.forEach((item, i) => {
        item.classList.toggle('blog-search__result-item--active', i === this.selectedIndex);
      });
    }

    handleClickOutside(e) {
      if (!this.contains(e.target)) {
        this.close();
      }
    }

    async fetchResults(query) {
      const cacheKey = `${this.blogHandle}:${query.toLowerCase()}`;
      if (this.cachedResults[cacheKey]) {
        this.renderResults(this.cachedResults[cacheKey]);
        return;
      }

      this.showLoading();

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'article',
        'resources[limit]': '10',
        'resources[options][fields]': 'title,tag',
      });

      try {
        const response = await fetch(`${this.searchUrl}?${params}`, {
          signal: this.abortController.signal,
        });
        if (!response.ok) throw new Error(response.status);

        const data = await response.json();
        const articles = (data.resources?.results?.articles || []).filter(
          (article) => article.url.startsWith(this.blogPrefix)
        );

        this.cachedResults[cacheKey] = articles;
        this.renderResults(articles);
      } catch (err) {
        if (err.name !== 'AbortError') {
          this.close();
        }
      }
    }

    renderResults(articles) {
      this.selectedIndex = -1;

      if (!articles.length) {
        const noResults = document.createElement('div');
        noResults.className = 'blog-search__no-results';
        // Uebersetzter Text kommt aus dem Markup, siehe supplementa-blog-search.liquid.
        noResults.textContent = this.dataset.noResultsText || '';
        this.results.replaceChildren(noResults);
        this.open();
        return;
      }

      const html = articles
        .map((article) => {
          const image = article.image
            ? `<img class="blog-search__result-image" src="${article.image}" alt="" width="48" height="48" loading="lazy">`
            : '<div class="blog-search__result-image blog-search__result-image--placeholder"></div>';

          const tags =
            article.tags && article.tags.length
              ? `<span class="blog-search__result-tag">${article.tags[0]}</span>`
              : '';

          return `<li class="blog-search__result-item">
            <a href="${article.url}" class="blog-search__result-link">
              ${image}
              <div class="blog-search__result-text">
                <span class="blog-search__result-title">${article.title}</span>
                ${tags}
              </div>
            </a>
          </li>`;
        })
        .join('');

      this.results.innerHTML = `<ul class="blog-search__result-list" role="listbox">${html}</ul>`;
      this.open();
    }

    showLoading() {
      this.results.innerHTML =
        '<div class="blog-search__loading"><span class="blog-search__spinner"></span></div>';
      this.open();
    }

    open() {
      this.results.hidden = false;
      this.classList.add('blog-search--open');
    }

    close() {
      this.results.hidden = true;
      this.classList.remove('blog-search--open');
      this.selectedIndex = -1;
    }
  }

  customElements.define('blog-search', BlogSearch);
}
